/**
 * Output filter for LOCAL shells whose prompts Apiboo configures (terminal.rs).
 * Moved verbatim out of `useEmbeddedTerminal` so a pane can own one per
 * session; behaviour is pinned by `tests/terminalShellFilter.test.js`, which
 * was written against the hook before the move.
 *
 * `createShellOutputFilter(shellKey, { sendCls })` returns
 *   `{ push(chunk) -> { data: string|null, cleared: boolean }, dispose() }`.
 * `data === null` means "swallow this chunk" (the startup gate is closed);
 * `cleared` means the chunk contained a screen clear, so the caller drops its
 * block-divider decorations.
 */

// Startup gate: for shells whose prompts we control (terminal.rs), the
// first OSC 133;A marker is the true start of the session — everything
// before it (cmd's REPL newline, banner remnants, cls repaints) is
// discarded so every engine opens with the caret block at the very top.
// Shells we don't configure (wsl, unix defaults) never emit the marker,
// so they bypass the gate; a failsafe timer opens it regardless in case
// a controlled shell somehow never prompts.
const MARKER = '\x1b]133;A';
export const GATED_SHELLS = new Set(['cmd', 'powershell', 'pwsh', 'gitbash']);
const CLEAR_SEQ = '\x1b[2J';
const FAILSAFE_MS = 2500;

// cmd prints its REPL CRLF between `cls` finishing and the next prompt,
// parking the caret on row 1 with row 0 blank — the same defect the
// startup gate fixes for fresh tabs, recreated by every manual cls.
// Between a screen-clear and the next prompt marker the only legitimate
// bytes are escape sequences, so stripping CR/LF in that window puts
// the prompt back at the top without touching anything else.
const stripNlUntilMarker = (segment) => {
  const m = segment.indexOf(MARKER);
  if (m === -1) return { text: segment.replace(/[\r\n]/g, ''), done: false };
  return { text: segment.slice(0, m).replace(/[\r\n]/g, '') + segment.slice(m), done: true };
};

export function createShellOutputFilter(shellKey, { sendCls } = {}) {
  // cmd needs TWO markers: on the first we send a real `cls` (which
  // scrubs cmd's REPL newline out of ConPTY's own screen model — the
  // gate alone only hides it from xterm, and any later repaint, e.g. a
  // resize, would resurrect it), then keep discarding until the clean
  // post-cls prompt. Other controlled shells open at their first marker.
  const gate = {
    active: GATED_SHELLS.has(shellKey),
    carry: '',
    needed: shellKey === 'cmd' ? 2 : 1,
    clsSent: false,
  };
  let failsafe = null;
  if (gate.active) {
    failsafe = setTimeout(() => {
      gate.active = false;
      gate.carry = '';
    }, FAILSAFE_MS);
  }
  const postClear = { active: false };

  // Trailing-blank normalizer: shells pad their output differently
  // (PowerShell's table formatter ends with TWO blank lines, cmd's REPL
  // adds one) — collapse any newline run that immediately precedes a
  // prompt marker to a single line terminator, so the block divider sits
  // directly under the last real output line in every engine. In-chunk
  // only: output and the following prompt arrive together in practice,
  // and holding back chunk-final newlines was a bug (cmd's prompt itself
  // ends in one — the caret rendered glued to the path).
  const push = (chunk) => {
    let data = chunk;
    if (gate.active) {
      const probe = gate.carry + data;
      let idx = -1;
      while (gate.needed > 0) {
        idx = probe.indexOf(MARKER, idx + 1);
        if (idx === -1) break;
        gate.needed--;
        if (shellKey === 'cmd' && !gate.clsSent && gate.needed === 1) {
          gate.clsSent = true;
          sendCls?.();
        }
      }
      if (gate.needed > 0) {
        // Keep a tail in case a marker splits across chunks.
        gate.carry = probe.slice(-MARKER.length);
        return { data: null, cleared: false };
      }
      gate.active = false;
      data = probe.slice(idx);
      gate.carry = '';
    }
    // Any screen clear (cmd cls, PS Clear-Host, bash clear — 2J/3J)
    // wipes the text: the caller drops the divider decorations with it and
    // lets the next prompt start a fresh "first block".
    const cleared = data.includes('\x1b[2J') || data.includes('\x1b[3J');
    if (shellKey === 'cmd') {
      const clearIdx = data.lastIndexOf(CLEAR_SEQ);
      if (clearIdx !== -1) {
        const at = clearIdx + CLEAR_SEQ.length;
        const res = stripNlUntilMarker(data.slice(at));
        data = data.slice(0, at) + res.text;
        postClear.active = !res.done;
      } else if (postClear.active) {
        const res = stripNlUntilMarker(data);
        data = res.text;
        postClear.active = !res.done;
      }
    }
    if (GATED_SHELLS.has(shellKey)) {
      // eslint-disable-next-line no-control-regex -- the ESC byte is what an OSC 133 marker is made of
      data = data.replace(/[\r\n]+(?=\x1b\]133;A)/g, '\r\n');
    }
    return { data, cleared };
  };

  const dispose = () => {
    if (failsafe) clearTimeout(failsafe);
    failsafe = null;
  };

  return { push, dispose };
}
